/**
 * Ticket lookups shared by the two replay entry points.
 *
 * A replay runs the whole pipeline inline, which can outlast the proxy's
 * patience: the browser gets a timeout while the server finishes anyway, and
 * the ticket row exists with nobody looking at it. Recovery is a lookup by
 * the Zendesk id we just typed — if the row is there, the work happened and
 * the only thing lost was the response.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiTicket } from '../lib/inbox/rows';
import { apiGet } from './client';

export async function findTicketByExternalId(externalId: string): Promise<string | null> {
  try {
    const tickets = await apiGet<Array<{ id: string; externalId: string }>>('/api/tickets?take=200');
    return tickets.find((ticket) => ticket.externalId === externalId)?.id ?? null;
  } catch {
    // The recovery probe failing must not mask the original error.
    return null;
  }
}

/** What the note says when even recovery found nothing yet. */
export const STILL_RUNNING_NOTE =
  'The response timed out but the run continues on the server — check the Inbox in a minute; the ticket appears there when it lands.';

/** GET /api/tickets/tiktok-waiting: TikTok chat replies waiting for a person's Send. */
export interface TikTokWaitingResponse {
  tickets: ApiTicket[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Under ['tickets'], so the ticket pane's refresh after a Send or a hand-off
 * takes the chat off every TikTok list and count (Deivydas 2026-10-26).
 */
export const tiktokWaitingKey = (query: string) => ['tickets', 'tiktok-waiting', query];

/** How many TikTok replies wait: the Approvals tab and the sidebar badge. */
export function useTikTokWaitingCount() {
  return useQuery({
    queryKey: tiktokWaitingKey(''),
    queryFn: () => apiGet<TikTokWaitingResponse>('/api/tickets/tiktok-waiting'),
    select: (data) => data.total,
    retry: false,
  });
}
